import React, { useRef, useState, useEffect } from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import { Storage } from 'aws-amplify';
import { GamifyToast } from 'application/common/CustomToasts';
import CloseIcon from '@mui/icons-material/Close';
import { useTheme } from '../../../../context/PermissionsContext/ThemeContext/ThemeContext';

interface ImageUploadProps {
  value: string | undefined;
  onChange: (imageKey: string) => void;
  label: string;
}

export const ImageUpload: React.FC<ImageUploadProps> = ({
  value,
  onChange,
  label,
}) => {
  const uploadRef = useRef<HTMLInputElement>(null);
  const [filePreview, setFilePreview] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const { theme } = useTheme();

  useEffect(() => {
    if (value) {
      // Display using CloudFront URL like damage reports
      const cloudFrontUrl = `https://d22xeeaijfewl2.cloudfront.net/public/${encodeURIComponent(
        value
      )}`;
      setFilePreview(cloudFrontUrl);
    } else {
      setFilePreview(null);
    }
  }, [value]);

  const handleUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      const file = files[0];
      const formats = ['jpeg', 'jpg', 'png', 'gif'];
      const fileName = file.name;
      const fileExtension = fileName.split('.').pop()?.toLowerCase();

      if (!fileExtension || !formats.includes(fileExtension)) {
        GamifyToast.error(
          'Image format must be one of the following: png, gif, jpeg, jpg'
        );
        return;
      }

      setIsUploading(true);
      try {
        // Follow the same pattern as damage reports
        // @ts-ignore
        const orgID = global?.me?.orgID;
        const storagePath = `company-info/${label
          .toLowerCase()
          .replace(/\s+/g, '-')}/${orgID}/${Date.now()}_${fileName}`;

        await Storage.put(storagePath, file, {
          contentType: file.type,
        });

        // Set preview using CloudFront URL
        const cloudFrontUrl = `https://d22xeeaijfewl2.cloudfront.net/public/${encodeURIComponent(
          storagePath
        )}`;
        setFilePreview(cloudFrontUrl);
        onChange(storagePath);
        GamifyToast.success('Image uploaded successfully!');
      } catch (error) {
        console.error('Error uploading image:', error);
        GamifyToast.error('Failed to upload image');
      } finally {
        setIsUploading(false);
      }
    }
  };

  const handleRemove = (e: React.MouseEvent) => {
    e.stopPropagation();
    setFilePreview(null);
    onChange('');
    if (uploadRef.current) {
      uploadRef.current.value = '';
    }
  };

  const triggerFileSelection = () => {
    if (uploadRef.current) {
      uploadRef.current.click();
    }
  };

  return (
    <Box>
      <Box
        onClick={triggerFileSelection}
        sx={{
          width: '100%',
          minHeight: filePreview ? 200 : 120,
          border: `2px dashed ${theme.stroke}`,
          borderRadius: '8px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          backgroundColor: theme.background2,
          position: 'relative',
          '&:hover': {
            borderColor: theme.textLight,
            backgroundColor: theme.background3,
          },
          transition: 'all 0.2s ease',
        }}
      >
        <input
          ref={uploadRef}
          type="file"
          accept="image/*"
          onChange={handleUpload}
          style={{ display: 'none' }}
        />

        {filePreview ? (
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: 2,
            }}
          >
            <IconButton
              onClick={handleRemove}
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 1)',
                },
              }}
              size="small"
            >
              <CloseIcon />
            </IconButton>
            <img
              src={filePreview}
              alt={label}
              style={{
                maxWidth: '100%',
                maxHeight: 180,
                objectFit: 'contain',
              }}
            />
          </Box>
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Box
              component="img"
              src="/images/upload-icon.svg"
              alt="Upload"
              sx={{ width: 40, height: 40, opacity: 0.5 }}
              onError={(e: any) => {
                e.target.style.display = 'none';
              }}
            />
            <Typography sx={{ fontSize: 14, color: theme.textLight }}>
              {isUploading ? 'Uploading...' : `Click to upload ${label}`}
            </Typography>
            <Typography
              sx={{ fontSize: 12, color: theme.isDark ? '#888888' : '#ABABAB' }}
            >
              PNG, JPG, GIF (max. 5MB)
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};
